import React, { useContext, useState } from "react";
import axios from "axios";
import { 
  Col, 
  Row, 
  Button, 
  Input,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap";
import ToolkitProvider, {
  CSVExport
} from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import SubUserFilterCustom from "./subUserFilterCustom";
import SubUserStatementTableDropdown from "./subuserStatementTableDropdown";
import { StateContext } from "../utils/context";
import TenantContext from "../utils/tenantContext";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import StatementPdfDownloadButton from "./statementPdfDownloadButton";
import { sendAuditEvent } from "../utils/auditTrail";
import { DEMO_FEATURE_UNAVAILABLE, isDemoLocked } from "../utils/demoConfig";

const demoLockedControl = (tooltipId, control) => (
  <>
    <span id={tooltipId}>{control}</span>
    <UncontrolledTooltip placement="top" trigger="hover" target={tooltipId}>
      {DEMO_FEATURE_UNAVAILABLE}
    </UncontrolledTooltip>
  </>
);

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const SubuserStatementTable = ({
  tableData,
  parentReportId,
  setSuccess,
  setError,
  setToggleModal,
  setDataModal,
  setLastAction,
  forceUpdate,
  setForce,
  searchQuery,
  defaultSorted,
  subUserData,
  auditData,
}) => {

  const { currentUser } = useContext(StateContext);
  const { tenant } = useContext(TenantContext);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState(null);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [markVisibleModalOpen, setMarkVisibleModalOpen] = useState(false);

  const { ExportCSVButton } = CSVExport;

  const token = auditData?.token;
  const hasParentReportFilter = parentReportId != null && parentReportId !== "";

  const bulkStatementEmailsPending = tableData.some(
    row => !row.subuser_statement_emails_sent,
  );

  const bulkVisibilityPending = tableData.some(
    row => row.visibility !== "active",
  );

  const axiosAuthHeaders = token
    ? {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json",
      }
    : null;

  const confirmBulkMarkVisible = async () => {
    if (
      !axiosAuthHeaders ||
      !bulkVisibilityPending ||
      !hasParentReportFilter
    ) {
      return;
    }
    setMarkVisibleModalOpen(false);
    setBulkBusy(true);
    setBulkFeedback(null);
    try {
      const res = await axios.post(
        `${API_URL}subuser-statements/bulk-make-visible/`,
        { parent_report: parentReportId },
        { headers: axiosAuthHeaders },
      );
      const detail =
        typeof res.data?.detail === "string"
          ? res.data.detail
          : "Statements marked as visible.";
      setBulkFeedback({
        type: "success",
        msg: detail,
      });
      if (typeof setForce === "function") setForce(p => p + 1);
    } catch (err) {
      const data = err.response?.data;
      let detail = null;
      if (typeof data === "object" && data?.detail != null) {
        detail = Array.isArray(data.detail)
          ? data.detail.join(" ")
          : String(data.detail);
      }
      setBulkFeedback({
        type: "danger",
        msg:
          detail ||
          (typeof data === "string" ? data : null) ||
          "Could not update visibility. Please try again.",
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkSendEmail = async () => {
    if (
      !axiosAuthHeaders ||
      !bulkStatementEmailsPending ||
      !hasParentReportFilter
    ) {
      return;
    }
    setSendEmailModalOpen(false);
    setBulkBusy(true);
    setBulkFeedback(null);
    try {
      const res = await axios.post(
        `${API_URL}subuser-statements/bulk-send-email/`,
        { parent_report: parentReportId },
        { headers: axiosAuthHeaders },
      );
      const detail =
        typeof res.data?.detail === "string"
          ? res.data.detail
          : "Emails queued for all relevant statements.";
      setBulkFeedback({
        type: "success",
        msg: detail,
      });
      if (typeof setForce === "function") setForce(p => p + 1);
    } catch (err) {
      const data = err.response?.data;
      let detail = null;
      if (typeof data === "object" && data?.detail != null) {
        detail = Array.isArray(data.detail)
          ? data.detail.join(" ")
          : String(data.detail);
      }
      const msg =
        detail ||
        (typeof data === "string" ? data : null) ||
        "Could not send email. Please try again.";
      setBulkFeedback({ type: "danger", msg });
    } finally {
      setBulkBusy(false);
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  const currencies = ["€", "$", "£"];

  const monthFormatter = (cell, row) => {
    cell = months[cell - 1];
    return cell;
  };

  const amountFormatter = (cell, row) => `${cell}${currencies[row.currency]}`;

  const statusFormatter = cell =>
    cell === 0 ? (
      <Button className="btn-status" disabled color="warning">
        Open
      </Button>
    ) : cell === 1 ? (
      <Button className="btn-status" disabled color="success">
        Paid
      </Button>
    ) : null;

  const visibilityFormatter = cell =>
    cell === "active" ? (
      <span className="fas fa-2x fa-check-circle text-success"></span>
    ) : cell === "inactive" ? (
      <span className="fas fa-2x fa-times-circle text-danger"></span>
    ) : null;

    const typeFormatter = cell =>
    cell === 1
      ? (cell = "Licensing")
      : cell === 2
      ? "Regular"
      : cell === 3
      ? "Audio Mastering - Services"
      : cell === 4
      ? "Vinyl Earnings"
      : cell === 5
      ? "Offset"
      : "Not known";

  const makeDownloadFormatter = type => (cell, row) => (
    <StatementPdfDownloadButton
      row={row}
      fileUrl={cell || null}
      type={type}
      onDownload={() => {
        sendAuditEvent({
          token: auditData?.token,
          eventType: 5,
          route: auditData?.route || window.location.pathname,
          entityType: auditData?.entityType || "subuser_statement",
          entityIds: [row.id],
          tenantContext: tenant,
        });
      }}
    />
  );

  const statementFileCsvFormatter = (cell) => cell || "";

    const actionFormatter = (cell, row) => {
        return (
          <SubUserStatementTableDropdown
            data={{ id: row.id, status: row.status, name: row.name, visibility: row.visibility }}
            setSuccess={setSuccess}
            setError={setError}
            setToggleModal={setToggleModal}
            setDataModal={setDataModal}
            setLastAction={setLastAction}
          />
        );
      };

  const columns = [
    {
      dataField: "sts_kind",
      text: "Type",
      sort: true,
      formatter: typeFormatter,
      csvFormatter: typeFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "year",
      text: "Year",
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "month",
      text: "Month",
      sort: true,
      formatter: monthFormatter,
      csvFormatter: cell => months[cell - 1],
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "issue_year",
      text: "Iss. Year",
      sort: true,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "issue_month",
      text: "Iss. Month",
      sort: true,
      formatter: monthFormatter,
      csvFormatter: cell => months[cell - 1],
      headerStyle: (colum, colIndex) => {
        return { width: "110px" };
      }
    },
    {
      dataField: "short_description",
      text: "Description",
      headerStyle: (colum, colIndex) => {
        return { width: "150px" };
      }
    },
    {
      dataField: "price",
      text: "Amount",
      sort: true,
      formatter: amountFormatter,
      csvFormatter: amountFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "110px" };
      }
    },
    {
      dataField: "visibility",
      text: "Visible",
      formatter: visibilityFormatter,
      sort: false,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      formatter: statusFormatter,
      csvFormatter: cell =>
        cell === 0 ? "Open" : cell === 1 ? "  Paid" : null,
      headerStyle: (colum, colIndex) => {
        return { width: "100px" };
      }
    },
    {
      dataField: "new_details",
      text: "Details",
      headerAlign: "center",
      align: "center",
      formatter: makeDownloadFormatter("details"),
      csvFormatter: statementFileCsvFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "105px" };
      }
    },
    {
      dataField: "new_overview",
      text: "Overview",
      headerAlign: "center",
      align: "center",
      formatter: makeDownloadFormatter("overview"),
      csvFormatter: statementFileCsvFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "105px" };
      }
    },
    {
      dataField: "action",
      text: "Action",
      formatter: actionFormatter,
      sort: false,
      csvExport: false,
      headerStyle: () => ({ width: "110px", minWidth: "110px" }),
      style: () => ({ width: "110px", minWidth: "110px" }),
    },

    //data for search
    {
      dataField: "status",
      sort: false,
      csvExport: false,
      hidden: true,
      formatter: cell => (cell === 0 ? "Open" : cell === 1 ? "Paid" : null)
    }
  ];

  const handleTableChange = (type, { sortField, sortOrder, data }) => {
    const ascDesc = sortOrder === "desc" ? "-" : "";
    if (sortField === "kind" || sortField === "sts_kind" || sortField === "month" || sortField === "year" || sortField === "issue_month" || sortField === "issue_year" || sortField === "price") {
      searchQuery.setOrdering({order: ascDesc, name: sortField, sort: sortField});
    }
    if (sortField === "name") {
      searchQuery.setOrdering({order: ascDesc, name: "id", sort: sortField});
    }
  }


  return (
    <div>
      {hasParentReportFilter && (
        <>
          <Modal
            isOpen={sendEmailModalOpen}
            centered
            toggle={() => !bulkBusy && setSendEmailModalOpen(false)}
          >
            <ModalHeader toggle={() => !bulkBusy && setSendEmailModalOpen(false)}>
              Send Statement Email
            </ModalHeader>
            <ModalBody>
              Are you sure that you want to send emails for all Subuser Statements? <br /><br />
              <b>Note:</b> This action can't be done again once the emails have been sent.
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                disabled={bulkBusy}
                onClick={() => setSendEmailModalOpen(false)}
              >
                Cancel
              </Button>
              <Button color="primary" disabled={bulkBusy} onClick={confirmBulkSendEmail}>
                Send
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            isOpen={markVisibleModalOpen}
            centered
            toggle={() => !bulkBusy && setMarkVisibleModalOpen(false)}
          >
            <ModalHeader toggle={() => !bulkBusy && setMarkVisibleModalOpen(false)}>
              Mark statements as visible
            </ModalHeader>
            <ModalBody>
              Mark <strong>all</strong> subuser statements as visible to
              subusers?
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                disabled={bulkBusy}
                onClick={() => setMarkVisibleModalOpen(false)}
              >
                Cancel
              </Button>
              <Button color="success" disabled={bulkBusy} onClick={confirmBulkMarkVisible}>
                Mark visible
              </Button>
            </ModalFooter>
          </Modal>
        </>
      )}
      <ToolkitProvider
        keyField="id"
        data={tableData}
        columns={columns}
        search={{
          searchFormatted: true
        }}
        exportCSV={ {
          fileName: "subuser-statements.csv",
        } }
      >
        {props => (
          <div>
            {bulkFeedback && (
              <Alert color={bulkFeedback.type} className="mb-2">
                {bulkFeedback.msg}
              </Alert>
            )}
            <Row>
              <Col>
                <div className="customSearch">
                  <Input placeholder="Search..." value={searchQuery.query} style={{marginBottom: 10}} onChange={e => searchQuery.setQuery(e.target.value)}/>
                </div>
              </Col>
              {
                currentUser.is_premium_user && <SubUserFilterCustom subUserData={subUserData} />
              }
              <Col>
                <div className="flexEndContainer">
                  {hasParentReportFilter && (
                    <>
                      {(() => {
                        const sendEmailButton = (
                          <Button
                            outline
                            color="primary"
                            className="mr-2 mb-2"
                            disabled={
                              isDemoLocked() ||
                              bulkBusy ||
                              !token ||
                              !bulkStatementEmailsPending
                            }
                            style={isDemoLocked() ? { pointerEvents: "none" } : undefined}
                            onClick={() => setSendEmailModalOpen(true)}
                          >
                            Send Email
                          </Button>
                        );
                        return isDemoLocked()
                          ? demoLockedControl(
                              "subuser-statement-send-email",
                              sendEmailButton,
                            )
                          : sendEmailButton;
                      })()}
                      <Button
                        outline
                        color="success"
                        className="mr-2 mb-2"
                        disabled={
                          bulkBusy ||
                          !token ||
                          !bulkVisibilityPending
                        }
                        onClick={() => setMarkVisibleModalOpen(true)}
                      >
                        Mark as Visible
                      </Button>
                    </>
                  )}
                  <ExportCSVButton
                    className="btn btn-outline-info mb-2"
                    {...props.csvProps}
                  >
                    Export CSV
                  </ExportCSVButton>
                </div>
              </Col>
            </Row>
            <div className="subuser-statement-table-wrap">
              <BootstrapTable
                {...props.baseProps}
                bordered={ false }
                remote={ { sort: true } }
                defaultSorted={defaultSorted}
                onTableChange={handleTableChange}
              />
            </div>
          </div>
        )}
      </ToolkitProvider>
    </div>
  );
};

export default SubuserStatementTable;
